import SignatureImage from "../../components/SignatureImage";
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import SignaturePad from "react-signature-canvas";
import {
  FaChevronDown,
  FaChevronUp,
  FaEye,
  FaEyeSlash,
  FaLock,
  FaSave,
  FaShieldAlt,
  FaSignature,
  FaUser,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import LoadingOverlay from "../../components/LoadingOverlay";
import "../../styles/Form.css";
import "../../styles/Signature.css";
import "../../styles/MyAccountPage.css";

function MyAccountPage({ selectedUserId = null, readOnly = false }) {
  const navigate = useNavigate();
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const sigPadRef = useRef(null);
  const adminMode = selectedUserId != null;
  const accountEndpoint = adminMode
    ? `${BASE_URL}/api/admin/accounts/${selectedUserId}`
    : `${BASE_URL}/api/user/account`;
  const [accountLoaded, setAccountLoaded] = useState(false);

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    profilePic: "",
    userid_no: "",
  });
  const [savedSignature, setSavedSignature] = useState(null);
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [showSignatureSection, setShowSignatureSection] = useState(true);
  const [showPasswordSection, setShowPasswordSection] = useState(true);
  const [profileImageBroken, setProfileImageBroken] = useState(false);
  const accountInitials = `${form.first_name?.[0] || ""}${
    form.last_name?.[0] || ""
  }`.toUpperCase();
  const profileImageUrl = form.profilePic
    ? `${BASE_URL}/uploads/profilepic/${form.profilePic}`
    : null;

  useEffect(() => {
    const loadAccount = async () => {
      try {
        const headers = {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        };
        const { data } = await axios.get(accountEndpoint, {
          headers,
        });
        let profilePic = data.profilePic || "";

        if (!profilePic) {
          const currentUserId = selectedUserId || localStorage.getItem("userId");
          if (currentUserId) {
            try {
              const profileResponse = await axios.get(
                `${BASE_URL}/api/users/${currentUserId}`,
                { headers },
              );
              profilePic = profileResponse.data?.profilePic || "";
            } catch (profileError) {
              console.warn("Failed to load account profile picture:", profileError);
            }
          }
        }

        setForm({
          first_name: data.first_name || "",
          last_name: data.last_name || "",
          email: data.email || "",
          profilePic,
          userid_no: data.userid_no || "",
        });
        setSavedSignature(data.user_signature || null);
        setAccountLoaded(true);
      } catch (err) {
        console.error("Failed to load account:", err);
        alert("Failed to load account details.");
      }
    };

    loadAccount();
  }, [BASE_URL, accountEndpoint, selectedUserId]);

  useEffect(() => {
    setProfileImageBroken(false);
  }, [form.profilePic]);

  const handleFormChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handlePasswordChange = (e) => {
    setPasswordForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const resolveSignatureToSave = () => {
    const isEditingSignature = showSignaturePad || !savedSignature;

    if (isEditingSignature) {
      if (sigPadRef.current?.isEmpty()) {
        return null;
      }

      return sigPadRef.current?.getCanvas().toDataURL("image/png");
    }

    return savedSignature || null;
  };

  const handleSaveAccount = async () => {
    if (readOnly || !accountLoaded) return;
    const signatureToSave = resolveSignatureToSave();
    const isEditingSignature = showSignaturePad || !savedSignature;

    if (isEditingSignature && !signatureToSave && (!adminMode || showSignaturePad)) {
      alert("Please draw your signature before saving.");
      return;
    }

    try {
      setLoading(true);
      await axios.put(
        accountEndpoint,
        {
          ...form,
          user_signature: signatureToSave,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      setSavedSignature(signatureToSave);
      setShowSignaturePad(false);
      alert(adminMode ? "User account updated successfully." : "My Account updated successfully.");
    } catch (err) {
      console.error("Account save failed:", err);
      alert(err?.response?.data?.message || "Failed to save My Account.");
    } finally {
      setLoading(false);
    }
  };

  const handleSavePassword = async () => {
    if (readOnly || !accountLoaded) return;
    if (
      (!adminMode && !passwordForm.currentPassword) ||
      !passwordForm.newPassword ||
      !passwordForm.confirmPassword
    ) {
      alert("Please complete all password fields.");
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      alert("New passwords do not match.");
      return;
    }

    if (adminMode && passwordForm.newPassword.length < 9) {
      alert("New password must be at least 9 characters.");
      return;
    }

    try {
      setSavingPassword(true);
      await axios.put(
        `${accountEndpoint}/password`,
        adminMode
          ? { newPassword: passwordForm.newPassword, confirmPassword: passwordForm.confirmPassword }
          : passwordForm,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      setPasswordForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      alert("Password updated successfully.");
    } catch (err) {
      console.error("Password save failed:", err);
      alert(err?.response?.data?.message || "Failed to update password.");
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <>
      {(loading || savingPassword) && (
        <LoadingOverlay
          text={loading ? "Saving account..." : "Updating password..."}
        />
      )}

      <div className="my-account-toolbar pt-3">
        <div className="my-account-toolbar-title">
          <FaUser aria-hidden="true" />
          <span>{adminMode ? "User Account" : "My Account"}</span>
        </div>
        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-success px-4"
            onClick={handleSaveAccount}
            disabled={loading || readOnly || !accountLoaded}
          >
            <FaSave className="me-2" aria-hidden="true" />
            Save Account
          </button>
          <button
            type="button"
            className="btn btn-secondary px-4"
            onClick={() => navigate(-1)}
          >
            Return
          </button>
        </div>
      </div>

      <fieldset disabled={readOnly || !accountLoaded} className="form-wrapper my-4 my-account-page">
        <div className="my-account-hero">
          <span className="my-account-hero-icon" aria-hidden="true">
            {profileImageUrl && !profileImageBroken ? (
              <img
                src={profileImageUrl}
                alt=""
                onError={() => setProfileImageBroken(true)}
              />
            ) : (
              accountInitials || <FaUser />
            )}
          </span>
          <div className="my-account-hero-copy">
            <span className="my-account-eyebrow">Account settings</span>
            <h2>{adminMode ? `${form.first_name} ${form.last_name}`.trim() || "User Account" : "Manage Your Account"}</h2>
            {adminMode && <p>User ID: {form.userid_no || selectedUserId}</p>}
            <p>{adminMode ? "Manage this user?s account information, signature, and password." : "Update your profile information, signature, and password."}</p>
          </div>
        </div>

        <section className="my-account-section">
          <div className="my-account-section-header">
            <span className="my-account-section-icon" aria-hidden="true">
              <FaUser />
            </span>
            <span className="my-account-section-heading">
              <strong>Profile Information</strong>
              <small>Update your name and contact email</small>
            </span>
          </div>
          <div className="my-account-section-body">
            <div className="row mb-3">
              <div className="col-md-4">
                <label className="form-label">
                  First Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="first_name"
                  className="form-control"
                  value={form.first_name}
                  onChange={handleFormChange}
                  required
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">
                  Last Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="last_name"
                  className="form-control"
                  value={form.last_name}
                  onChange={handleFormChange}
                  required
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">
                  Email <span className="text-danger">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  className="form-control"
                  value={form.email}
                  onChange={handleFormChange}
                  required
                />
              </div>
            </div>
          </div>
        </section>

        <section className="my-account-section">
          <button
            type="button"
            className="my-account-section-header my-account-section-toggle"
            onClick={() => setShowSignatureSection((prev) => !prev)}
          >
            <span className="my-account-section-icon" aria-hidden="true">
              <FaSignature />
            </span>
            <span className="my-account-section-heading">
              <strong>{adminMode ? "User Signature" : "Your Signature"}</strong>
              <small>Manage the signature used on your documents</small>
            </span>
            <span className="my-account-toggle-icon">
              {showSignatureSection ? <FaChevronUp /> : <FaChevronDown />}
            </span>
          </button>
          {showSignatureSection && <div className="my-account-section-body">
          <p className="my-account-section-intro">
            {adminMode
              ? "This user's saved signature will be available for future task signing."
              : "Your saved signature will be available when completing forms and signing documents."}
          </p>

          {!showSignaturePad && savedSignature ? (
            <div className="my-account-signature-workspace">
              <SignatureImage
                src={savedSignature}
                alt="Saved signature"
              />
              <div className="signature-buttons mt-2">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowSignaturePad(true)}
                >
                  {adminMode ? "Re-do Signature" : "Re-do My Signature"}
                </button>
              </div>
            </div>
          ) : (
            <div className="signature-container my-account-signature-workspace">
              <SignaturePad
                ref={sigPadRef}
                canvasProps={{ className: "sigCanvas" }}
              />
              <div className="signature-buttons mt-2">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => sigPadRef.current?.clear()}
                >
                  Clear Signature
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={handleSaveAccount}
                  disabled={loading || readOnly || !accountLoaded}
                >
                  {loading ? "Saving..." : "Save Signature"}
                </button>
                {savedSignature && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setShowSignaturePad(false)}
                  >
                    Keep Existing Signature
                  </button>
                )}
              </div>
            </div>
          )}
          </div>}
        </section>

        <section className="my-account-section">
          <button
            type="button"
            className="my-account-section-header my-account-section-toggle"
            onClick={() => setShowPasswordSection((prev) => !prev)}
          >
            <span className="my-account-section-icon" aria-hidden="true">
              <FaLock />
            </span>
            <span className="my-account-section-heading">
              <strong>Password &amp; Security</strong>
              <small>{adminMode ? "Reset this user?s password without their current password" : "Choose a strong password for your account"}</small>
            </span>
            <span className="my-account-toggle-icon">
              {showPasswordSection ? <FaChevronUp /> : <FaChevronDown />}
            </span>
          </button>
          {showPasswordSection && <div className="my-account-section-body">
            <div className="my-account-password-grid">
              <div>
                {!adminMode && (
                  <div className="mb-2">
                    <label className="form-label" htmlFor="currentPassword">
                      Current Password
                    </label>
                    <div className="my-account-password-field">
                      <input
                        id="currentPassword"
                        type={passwordVisible ? "text" : "password"}
                        name="currentPassword"
                        className="form-control my-account-password-input"
                        value={passwordForm.currentPassword}
                        onChange={handlePasswordChange}
                        placeholder="Enter your current password"
                      />
                      <button
                        type="button"
                        className="my-account-password-toggle"
                        data-no-standard-button="true"
                        onClick={() => setPasswordVisible((prev) => !prev)}
                        aria-label={passwordVisible ? "Hide passwords" : "Show passwords"}
                      >
                        {passwordVisible ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                  </div>
                )}
                <div className="mb-2">
                  <label className="form-label" htmlFor="newPassword">
                    New Password
                  </label>
                  <div className="my-account-password-field">
                  <input
                    id="newPassword"
                    type={passwordVisible ? "text" : "password"}
                    name="newPassword"
                    className="form-control my-account-password-input"
                    value={passwordForm.newPassword}
                    onChange={handlePasswordChange}
                    placeholder="Enter a new password"
                  />
                  {adminMode && (
                    <button
                      type="button"
                      className="my-account-password-toggle"
                      data-no-standard-button="true"
                      onClick={() => setPasswordVisible((prev) => !prev)}
                      aria-label={passwordVisible ? "Hide passwords" : "Show passwords"}
                    >
                      {passwordVisible ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  )}
                  </div>
                </div>
                <div>
                  <label className="form-label" htmlFor="confirmPassword">
                    Confirm New Password
                  </label>
                  <input
                    id="confirmPassword"
                    type={passwordVisible ? "text" : "password"}
                    name="confirmPassword"
                    className="form-control my-account-password-input"
                    value={passwordForm.confirmPassword}
                    onChange={handlePasswordChange}
                    placeholder="Enter the new password again"
                  />
                </div>
              </div>

              <div className="my-account-password-help">
                <div className="my-account-password-help-accent">
                  <FaShieldAlt aria-hidden="true" />
                </div>
                <div className="my-account-password-help-content">
                  <strong>Suggestions to create a strong password:</strong>
                  <ul>
                    <li>Your password must be a minimum of 9 characters in length</li>
                    <li>Avoid patterns and series of numbers and letters</li>
                    <li>Try a sentence or easily memorable series of words separated by spaces</li>
                    <li>Special characters are not required</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="my-account-password-button-row">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSavePassword}
                disabled={savingPassword}
              >
                {adminMode ? "Reset Password" : "Change Password"}
              </button>
            </div>
          </div>}
        </section>
      </fieldset>
    </>
  );
}

export default MyAccountPage;
